import {
    Box,
    Button,
    ButtonProps,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    styled,
    TextField,
    Typography,
} from '@mui/material';
import _ from 'lodash';
import React, { ChangeEvent, ElementType } from 'react';
import { v4 as uuid } from 'uuid';
import { loadOptionForCustomer, loadOptionsForSprint, OptionsStore } from '../../common/OptionsLoader';
import { SelectOption } from 'src/types/common';
import CustomDatePicker from '../../common/CustomDatePicker';
import { useNavigate } from 'react-router-dom';
import { client } from '../../../utils/axios-client';
import { ApiResponseType } from '../../../types';
import { leadDataManagementEndpoint } from '../../../configs/endpoints';
import useExcelImport from './import.leads.hooks';
import ExcelImportStore from './ExcelImportStore';
import { Session } from './LeadDataGridStore';

type Props = {
    importing: boolean;
    onClose: (cancelled: boolean) => void;
};

const LeadImportDialog = ({ onClose, importing }: Props) => {
    const unmappedColumns = ExcelImportStore.useState((s) => s.unmappedColumns);

    const session = ExcelImportStore.useState((s) => s.session);
    const customers = OptionsStore.useState((s) => s.customers);
    const stages = OptionsStore.useState((s) => s.stages);
    const [customerSprints, setCustomerSprints] = React.useState<SelectOption[]>([]);
    const [sprintCadences, setSprintCadences] = React.useState<SelectOption[]>([]);
    const [errorOccured, setErrorOccured] = React.useState(false);
    const navigate = useNavigate();

    React.useEffect(() => {
        if (_.isEmpty(session.sessionId)) {
            ExcelImportStore.update((s) => {
                const stageId = stages.find((p) => p.name === 'Prospecting')?.value;

                s.session = {
                    importType: 'ImportToSprint',
                    name: '',
                    sessionId: uuid(),
                } as Session;
            });
        }
    }, [session]);

    React.useEffect(() => {
        const loadOptions = async () => {
            const response = await loadOptionForCustomer('sprint', session.customerId);
            setCustomerSprints(response);
        };

        loadOptions();
    }, [session.customerId]);

    React.useEffect(() => {
        const loadOptions = async () => {
            const response = await loadOptionsForSprint(session.sprintId);
            setSprintCadences(response);
        };

        loadOptions();
    }, [session.sprintId]);

    const onSave = async () => {
        let hasError = false;
        ExcelImportStore.update((s) => {
            s.session.nameError = false;
            s.session.startDateError = false;
            s.session.cadenceError = false;
            s.session.customerError = false;
            s.session.sprintError = false;
        });

        if (_.isEmpty(session.name)) {
            ExcelImportStore.update((s) => {
                s.session.nameError = true;
            });

            hasError = true;
        }

        if (_.isEmpty(session.customerId)) {
            ExcelImportStore.update((s) => {
                s.session.customerError = true;
            });

            hasError = true;
        }

        if (session.importType === 'ImportToSprint') {
            if (_.isEmpty(session.cadenceId)) {
                ExcelImportStore.update((s) => {
                    s.session.cadenceError = true;
                });

                hasError = true;
            }

            if (_.isEmpty(session.sprintId)) {
                ExcelImportStore.update((s) => {
                    s.session.sprintError = true;
                });

                hasError = true;
            }

            if (_.isEmpty(session.startDate)) {
                ExcelImportStore.update((s) => {
                    s.session.startDateError = true;
                });

                hasError = true;
            }
        }

        if (hasError) return;

        const response = await client.post<ApiResponseType>(leadDataManagementEndpoint + '/savesession', session);

        if (response.data.succeeded) {
            onClose(false);
        } else {
            setErrorOccured(true);
        }
    };

    const onCancel = () => {
        if (importing) {
            onClose(true);
            return;
        }

        onClose(true);
    };

    if (stages.length === 0) return null;

    return (
        <Box>
            <DialogContent>
                <Grid container rowGap={4} spacing={2}>
                    <Grid xs={12}>
                        <Typography variant="h6">Import Settings</Typography>
                        {!importing && (
                            <Typography variant="body2">
                                You will be able to change your answers before commiting leads to Nostra.
                            </Typography>
                        )}
                        {errorOccured && (
                            <Typography sx={{ color: 'red' }} variant="body2">
                                An error occured while trying to save the sesssion
                            </Typography>
                        )}
                    </Grid>

                    <Grid xs={12}>
                        <FormControl>
                            <FormLabel>Import Type</FormLabel>
                            <RadioGroup
                                row
                                name="import-type"
                                value={session.importType}
                                onChange={(e) =>
                                    ExcelImportStore.update((s) => {
                                        s.session.importType = e.target.value;
                                    })
                                }
                            >
                                <FormControlLabel value="ImportOnly" control={<Radio />} label="Import Only" />
                                <FormControlLabel value="ImportToSprint" control={<Radio />} label="Assign to Sprint" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    <Grid xs={12}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Import Name"
                            error={session.nameError}
                            value={session.name}
                            onChange={(e) =>
                                ExcelImportStore.update((s) => {
                                    s.session.name = e.target.value;
                                })
                            }
                        />
                    </Grid>

                    <Grid xs={12}>
                        <FormControl size="small" fullWidth error={session.customerError}>
                            <InputLabel>Customer</InputLabel>
                            <Select
                                label="Customer"
                                value={session.customerId}
                                onChange={(e) =>
                                    ExcelImportStore.update((s) => {
                                        s.session.customerId = e.target.value;
                                    })
                                }
                            >
                                {customers.map((item) => (
                                    <MenuItem value={item.value}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {session.importType === 'ImportToSprint' && (
                        <>
                            <Grid xs={12}>
                                <FormControl
                                    size="small"
                                    fullWidth
                                    error={session.sprintError && !_.isEmpty(session.customerId)}
                                    disabled={_.isEmpty(session.customerId)}
                                >
                                    <InputLabel>Sprint</InputLabel>
                                    <Select
                                        label="Sprint"
                                        value={session.sprintId}
                                        onChange={(e) =>
                                            ExcelImportStore.update((s) => {
                                                s.session.sprintId = e.target.value;
                                            })
                                        }
                                    >
                                        {customerSprints.map((item) => (
                                            <MenuItem value={item.value}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid xs={12}>
                                <FormControl
                                    size="small"
                                    fullWidth
                                    error={session.cadenceError && !_.isEmpty(session.sprintId)}
                                    disabled={_.isEmpty(session.sprintId)}
                                >
                                    <InputLabel>Cadence</InputLabel>
                                    <Select
                                        label="Cadence"
                                        value={session.cadenceId}
                                        onChange={(e) =>
                                            ExcelImportStore.update((s) => {
                                                s.session.cadenceId = e.target.value;
                                            })
                                        }
                                    >
                                        {sprintCadences.map((item) => (
                                            <MenuItem value={item.value}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/*<Grid xs={12}>*/}
                            {/*    {session.stageId && <FormControl size='small' fullWidth error={session.stageError}>*/}
                            {/*        <InputLabel>Stage</InputLabel>*/}
                            {/*        <Select label="Stage" value={session.stageId} onChange={(e) => Store.update(s => { s.session.stageId = e.target.value })}>*/}
                            {/*            {stages.map(item => <MenuItem value={item.value}>{item.name}</MenuItem>)}*/}
                            {/*        </Select>*/}
                            {/*    </FormControl>}*/}
                            {/*</Grid>*/}

                            <Grid xs={12}>
                                <CustomDatePicker
                                    label="Start Date"
                                    value={session.startDate}
                                    onChange={(value) =>
                                        ExcelImportStore.update((s) => {
                                            s.session.startDate = value;
                                        })
                                    }
                                    error={session.startDateError}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button size="small" variant="contained" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    onClick={onSave}
                    autoFocus
                    disabled={unmappedColumns.length > 0}
                >
                    {importing ? 'Import' : 'Continue'}
                </Button>
            </DialogActions>
        </Box>
    );
};

export default LeadImportDialog;
